import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Wrench, Building2, Truck, Users, History, Settings as SettingsIcon,
  Menu, Search, PlusCircle, LogOut, ChevronLeft, ChevronRight, ChevronDown, User as UserIcon,
  FileText, Bell, FileSpreadsheet, Boxes, Calculator, Clock, CheckCircle2, Archive, FileEdit,
  LayoutList, Wallet, BookOpen, UserCog, PackageOpen
} from "lucide-react";
import { CAN_CREATE_PROJECT } from "@/lib/constants";

// Role constants
const OWNER = "Owner", MANAGER = "Manager", MARKETING = "Marketing", KAPROD = "Kepala Produksi",
  FOREMAN = "Foreman", FINANCE = "Finance", ADMIN = "Admin Project", TEKNISI = "Teknisi",
  PURCHASING = "Purchasing", WAREHOUSE = "Warehouse", SECURITY = "Security";
const ALL = [OWNER, MANAGER, MARKETING, KAPROD, FOREMAN, FINANCE, ADMIN, TEKNISI, PURCHASING, WAREHOUSE, SECURITY];

export const ROLE_LABELS = { "Kepala Produksi": "Head Produksi" };
export const roleLabel = (r) => ROLE_LABELS[r] || r;

// Grouped navigation. Menu is role-driven; only existing/built pages are listed.
// Unbuilt modules (Purchasing, Warehouse, Finance suite, Reports, etc.) are intentionally hidden until built.
const NAV = [
  { type: "item", id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/", roles: ALL },
  {
    type: "group", id: "project", heading: "PROJECT",
    items: [
      { label: "Semua Proyek", path: "/projects", icon: LayoutList, roles: [OWNER, MANAGER, MARKETING, FINANCE, ADMIN, KAPROD, FOREMAN] },
      { label: "Draft", path: "/projects?stage=Draft", icon: FileEdit, roles: [OWNER, MANAGER, MARKETING, ADMIN] },
      { label: "Penawaran", path: "/estimates", icon: FileSpreadsheet, roles: [OWNER, MANAGER, MARKETING, FINANCE, ADMIN] },
      { label: "Menunggu Persetujuan", path: "/projects?stage=Menunggu Persetujuan", icon: Clock, roles: [OWNER, MANAGER, MARKETING, ADMIN] },
      { label: "On Production", path: "/projects?stage=On Production", icon: Wrench, roles: [OWNER, MANAGER, MARKETING, KAPROD, FOREMAN, ADMIN] },
      { label: "Ready", path: "/projects?stage=Ready", icon: CheckCircle2, roles: [OWNER, MANAGER, MARKETING, KAPROD, FOREMAN, ADMIN] },
      { label: "Unit Out", path: "/projects?stage=Unit Out", icon: Truck, roles: [OWNER, MANAGER, MARKETING, KAPROD, ADMIN] },
      { label: "Closed", path: "/projects?stage=Closed", icon: Archive, roles: [OWNER, MANAGER, ADMIN, FINANCE] },
      { label: "RAB Bahan", path: "/rab", icon: Boxes, roles: [OWNER, MANAGER, FINANCE, ADMIN, KAPROD] },
      { label: "HPP / Material Sheet", path: "/hpp", icon: Calculator, roles: [OWNER, MANAGER, FINANCE, KAPROD] },
    ],
  },
  {
    type: "group", id: "operasional", heading: "OPERASIONAL",
    items: [
      { label: "Material Usage", path: "/material-usage", icon: PackageOpen, roles: [OWNER, MANAGER, ADMIN, KAPROD, FOREMAN, TEKNISI] },
      { label: "Sparepart Proyek", path: "/spareparts", icon: Boxes, roles: [OWNER, MANAGER, FINANCE, KAPROD, PURCHASING, WAREHOUSE] },
      { label: "Laporan Hari Ini", path: "/daily-reports?scope=today", icon: FileText, roles: [OWNER, MANAGER, KAPROD, FOREMAN, TEKNISI] },
      { label: "Menunggu Persetujuan", path: "/daily-reports?scope=pending", icon: Clock, roles: [OWNER, MANAGER, KAPROD, FOREMAN, TEKNISI] },
      { label: "Riwayat Laporan", path: "/daily-reports?scope=history", icon: History, roles: [OWNER, MANAGER, KAPROD, FOREMAN, TEKNISI] },
    ],
  },
  {
    type: "group", id: "finance", heading: "FINANCE",
    items: [
      { label: "Petty Cash", path: "/petty-cash", icon: Wallet, roles: [OWNER, MANAGER, FINANCE] },
      { label: "Jurnal Umum", path: "/jurnal-umum", icon: BookOpen, roles: [OWNER, MANAGER, FINANCE] },
    ],
  },
  {
    type: "group", id: "master", heading: "MASTER DATA",
    items: [
      { label: "Master Customer", path: "/customers", icon: Building2, roles: [OWNER, MANAGER, MARKETING, ADMIN] },
      { label: "Master Kendaraan", path: "/vehicles", icon: Truck, roles: [OWNER, MANAGER, MARKETING, ADMIN] },
      { label: "Master Karyawan", path: "/employees", icon: Users, roles: [OWNER, MANAGER, ADMIN] },
    ],
  },
  {
    type: "group", id: "sistem", heading: "SISTEM",
    items: [
      { label: "Users & Roles", path: "/users", icon: UserCog, roles: [OWNER] },
      { label: "Audit History", path: "/audit", icon: History, roles: [OWNER, MANAGER, ADMIN, KAPROD] },
      { label: "Settings", path: "/settings", icon: SettingsIcon, roles: [OWNER] },
    ],
  },
];

const roleOk = (roles, user) => !roles || (user && roles.includes(user.role));

function isItemActive(path, loc) {
  const [p, q] = path.split("?");
  if (q) return loc.pathname === p && loc.search === "?" + q;
  if (loc.pathname === p) return loc.search === "";
  return loc.pathname.startsWith(p + "/");
}

function SidebarContent({ collapsed, onNavigate, openMap, setOpenMap, singleOpen }) {
  const { user } = useAuth();
  const location = useLocation();

  const toggleGroup = (id) => {
    setOpenMap((m) => {
      const isOpen = !!m[id];
      if (singleOpen) return isOpen ? {} : { [id]: true };
      return { ...m, [id]: !isOpen };
    });
  };

  // Visible groups/items after role filtering
  const visibleNav = NAV
    .filter((n) => roleOk(n.roles, user))
    .map((n) => (n.type === "group" ? { ...n, items: n.items.filter((it) => roleOk(it.roles, user)) } : n))
    .filter((n) => n.type !== "group" || n.items.length > 0);

  return (
    <div className="flex flex-col h-full bg-navy text-slate-100" style={{ backgroundColor: "#0F172A" }}>
      <div className="flex items-center gap-2.5 px-4 h-16 border-b border-white/10 shrink-0 sticky top-0 bg-navy z-10">
        <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center font-heading font-extrabold text-white text-lg shrink-0">K3</div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-heading font-bold text-white text-sm">K3 REPAIR SYSTEM</div>
            <div className="text-[10px] text-slate-400">Commercial Vehicle Repair</div>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {visibleNav.map((node) => {
          // Standalone item (Dashboard)
          if (node.type === "item") {
            const Icon = node.icon;
            const active = isItemActive(node.path, location);
            return (
              <NavLink key={node.id} to={node.path} onClick={onNavigate} data-testid={`nav-${node.id}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${active ? "bg-primary text-white" : "text-slate-100 hover:bg-[#1E293B]"} ${collapsed ? "justify-center" : ""}`}
                title={collapsed ? node.label : undefined}>
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span>{node.label}</span>}
              </NavLink>
            );
          }

          // Collapsed rail: render group items as icon-only links (no headings)
          if (collapsed) {
            return (
              <div key={node.id} className="space-y-1 border-t border-white/10 pt-1 first:border-0 first:pt-0">
                {node.items.map((s) => {
                  const SIcon = s.icon;
                  const active = isItemActive(s.path, location);
                  return (
                    <NavLink key={s.path} to={s.path} onClick={onNavigate}
                      className={`flex items-center justify-center px-3 py-2 rounded-md transition-colors ${active ? "bg-primary text-white" : "text-slate-100 hover:bg-[#1E293B]"}`}
                      title={s.label}>
                      <SIcon size={18} className="shrink-0" />
                    </NavLink>
                  );
                })}
              </div>
            );
          }

          // Expanded group with heading + collapse
          const open = !!openMap[node.id];
          return (
            <div key={node.id} className="pt-1">
              <button data-testid={`navgroup-${node.id}`} onClick={() => toggleGroup(node.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider text-slate-400 hover:text-slate-200 transition-colors">
                <span className="flex-1 text-left">{node.heading}</span>
                <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="mt-0.5 space-y-0.5">
                  {node.items.map((s) => {
                    const SIcon = s.icon;
                    const active = isItemActive(s.path, location);
                    return (
                      <NavLink key={s.path} to={s.path} onClick={onNavigate} data-testid={`nav-${node.id}-${s.path}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${active ? "bg-primary text-white" : "text-slate-200 hover:bg-[#1E293B] hover:text-white"}`}>
                        <SIcon size={16} className="shrink-0" />
                        <span className="flex-1">{s.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [openMap, setOpenMap] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("k3_sidebar_groups")); if (s && typeof s === "object") return s; } catch {}
    return { project: true };
  });
  useEffect(() => { try { localStorage.setItem("k3_sidebar_groups", JSON.stringify(openMap)); } catch {} }, [openMap]);
  useEffect(() => { api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {}); }, []);
  const unread = notifs.filter((n) => !n.read).length;
  const openNotifs = async () => {
    if (unread) { try { await api.post("/notifications/read-all"); } catch {} setNotifs((ns) => ns.map((n) => ({ ...n, read: true }))); }
  };

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/projects?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col shrink-0 transition-all duration-300 ${collapsed ? "w-16" : "w-64"}`}>
        <SidebarContent collapsed={collapsed} openMap={openMap} setOpenMap={setOpenMap} singleOpen={false} />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" style={{ backgroundColor: "#0F172A" }}
          className="p-0 w-[85%] max-w-[320px] border-0 z-[70] bg-navy text-white [&>button]:text-white [&>button]:opacity-90 [&>button]:z-50">
          <SheetTitle className="sr-only">Menu Navigasi</SheetTitle>
          <SheetDescription className="sr-only">Navigasi utama K3 Repair System</SheetDescription>
          <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} openMap={openMap} setOpenMap={setOpenMap} singleOpen={true} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 bg-white border-b flex items-center gap-2 px-3 sm:px-4">
          <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
            <Menu size={20} />
          </Button>
          <Button variant="ghost" size="icon" className="hidden md:flex" data-testid="toggle-sidebar-btn" onClick={() => setCollapsed((v) => !v)}>
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Button>
          <form onSubmit={doSearch} className="flex-1 max-w-md relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="global-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari Project ID, No. Polisi, customer..." className="pl-9 h-9 bg-slate-50" />
          </form>
          <div className="flex items-center gap-2">
            <DropdownMenu onOpenChange={(o) => o && openNotifs()}>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 rounded-md hover:bg-slate-100" data-testid="notifications-btn">
                  <Bell size={18} className="text-slate-600" />
                  {unread > 0 && <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[9px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unread}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                <DropdownMenuLabel>Notifikasi</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 ? <div className="px-3 py-6 text-center text-xs text-muted-foreground">Belum ada notifikasi.</div> : (
                  notifs.map((n) => (
                    <div key={n.id} data-testid="notif-item" className="px-3 py-2 border-b last:border-0">
                      <div className="text-xs font-semibold text-navy">{n.action}</div>
                      <div className="text-xs text-slate-600">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{n.project_id} · {n.plate} · {n.actor_name} · {fmtDateTime(n.created_at)}</div>
                    </div>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {user && CAN_CREATE_PROJECT.includes(user.role) && (
              <Button data-testid="header-new-project-btn" onClick={() => navigate("/projects/new")} className="hidden sm:flex bg-primary hover:bg-red-700 h-9">
                <PlusCircle size={16} className="mr-1.5" /> Buat Proyek
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100">
                  <div className="h-8 w-8 rounded-full bg-navy text-white flex items-center justify-center text-xs font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-xs font-semibold text-slate-800">{user?.name}</div>
                    <div className="text-[10px] text-primary font-medium">{roleLabel(user?.role)}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <div className="text-xs">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <UserIcon size={14} className="mr-2" /> Pengaturan
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="text-primary">
                  <LogOut size={14} className="mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
